import { getUUID } from '@dmb/core';

import { ReleaseDateData } from '../../product/wizard/release-date/release-date.model';
import { ProductType } from '../../product/wizard/type/product-type-select.component';
import {
  ContributorAssignment,
  ContributorAssignmentAndEntry,
  ContributorEntry,
  ContributorRole,
} from './contributors';
import { ContractSelection, CountrySelection, NeighbouringRightsSelection, ShopSelection } from './countries-shops';
import { CoverImage, Genre, UploadInfo } from './general';
import { CommonTrackMetadata, Explicitness, OverallMetadata, TrackMetadata } from './track-metadata';
import { Track } from './tracks';

type AsyncTitleValidation = { pending: boolean; errors: null | string[]; warnings: null | string[]; explicit: boolean };
export const productTypes = [
  'SINGLE',
  'EP',
  'ALBUM',
  'COMPILATION',
  'VIDEO_BUNDLE',
  'AUDIOBOOK', // Hörbuch
  'RADIO_PLAY', // Hörspiel
  'STEM',
] as const;

export enum UploadErrorType {
  UNKNOWN,
  FILE_TOO_BIG,
}

export type ProductData = {
  title: string;
  titleVersion: string;
  coverImages: CoverImage[];
  label: string;
  eanUpc: string;
  catalogNumber: string;
  language: string;
  genre: (Genre | undefined)[];
  pYear: string;
  pText: string;
  cYear: string;
  cText: string;
  description: string;
  price: string;
  // Spoken word specific fields
  seriesName: string;
  seriesNumber: string;
  bookletTitle: string;
  bookletCYear: string;
  bookletCText: string;
  bisacGenre: (Genre | undefined)[];
  wgsGenre: (Genre | undefined)[];
  ageRating: string;
  version: string;
  reviewQuotes: string;
  keywords: string;
  digitalPrice: string;
};
export type WizardFeatureState = {
  dbLoading: boolean;
  value: DmbWizardValue;
  asyncValidations: {
    eanUpc: { pending: boolean; errors: null | string };
    productTitle: AsyncTitleValidation;
    productTitleVersion: AsyncTitleValidation;
    tracks: Record<
      string,
      | {
          general: { pending: boolean; errors: null | string[] };
          isrc: { pending: boolean; error: null | string };
          title: AsyncTitleValidation;
          titleVersion: AsyncTitleValidation;
        }
      | undefined
    >;
    trackWasAnalyzed: Record<string, boolean>;
    tracksPreview: { pending: boolean; errors: null | string[] };
    coverImages: { pending: boolean; allErrors: { id: string; errors: null | string[] }[] };
  };
  uploadProgress: Record<string, { progress: number; error: UploadErrorType | undefined }>;
  trackDeletePending: (string | number)[];
  // Existing Track data is not saved, to avoid stale values
  existingTrackData: Record<
    number,
    'pending' | 'error' | { metadata: TrackMetadata; contributors: ContributorAssignmentAndEntry[] }
  >;
  constraints: {
    loading: boolean;
    error: boolean;
    canGenerateEAN: boolean;
    scriptLanguages: { value: string; label: string }[];
    audioLanguages: { value: string; label: string }[];
    roles: ContributorRole[];
    rolesRequiringStoreIds: string[];
    preselectedRoles: string[];
    priceCodes: { code: string; name: string; consumer_price_min: number; consumer_price_max: number }[];
    itunesPriceCodes: { code: string; name: string; dealer_price: number }[];
    ageRatingRequired: boolean;
    bookletCLineRequired: boolean;
    canHaveThirdPartyAccounting: boolean;
    canHaveNeighbouringRights: boolean;
    allowedCountries: string[];
    minTrackCount: number | undefined;
    minCoverWidth: number | undefined;
    minCoverHeight: number | undefined;
    canGenerateIsrc: boolean;
    clientId: string;
  };
};

// IMPORTANT: if anything here changes, a new PRODUCT_WIZARD_STORE version is needed
// New fields that should carry over between products also need an entry in getTemplateFields()
// (product/wizard/template/template-capture.ts), or label presets won't save/apply them.
export interface DmbWizardValue {
  id: string;
  lastModified: number;
  stepProgress: WizardStep | undefined;
  eanWasGenerated: boolean;
  productType: ProductType | undefined;
  productData: ProductData;
  /**
   * If a user deletes a track, the deletion can be undone for a short time period.
   * During this time period, the track is pending. It is still part of tracklisting
   * and its id is in state.trackDeletePending.
   * Existing tracks are never pending, they are deleted immediately.
   */
  tracklisting: Track[];
  tracksProductTitleAdded: boolean;
  tracksPreview: { id: string; file: File; uploadInfo: UploadInfo } | undefined;
  releaseDates: ReleaseDateData;
  contributors: ContributorEntry[];
  productContributors: ContributorAssignment[];
  overallMetadata: OverallMetadata;
  countrySelection: CountrySelection;
  shopSelection: ShopSelection;
  neighbouringRightsSelection: NeighbouringRightsSelection;
  contractSelection: ContractSelection;
}

// Defaults

const defaultCommonTrackMetadata: CommonTrackMetadata = {
  genre: [undefined],
  bisacGenre: [undefined],
  wgsGenre: [undefined],
  titleLanguage: '',
  lyricsLanguage: '',
  explicit: Explicitness.NON_EXPLICIT,
  pYear: '',
  pText: '',
  publisher: [''],
  instrumental: false,
  live: false,
  publicDomain: false,
  coverVersion: false,
  acapella: false,
  remastered: false,
  remasteredYear: '',
  noSpeaker: false,
};

export const defaultTrackMetadata: TrackMetadata = {
  ...defaultCommonTrackMetadata,
  snippetStart: '',
  snippetDuration: '',
  lyrics: '',
  aiUsageToggle: true,
  aiUsageLyrics: '',
  aiUsageAudio: '',
  aiUsageComposition: '',
  aiUsageVideo: '',
};

export const defaultTrackValidation: NonNullable<WizardFeatureState['asyncValidations']['tracks'][string]> = {
  title: { pending: false, errors: null, warnings: null, explicit: false },
  titleVersion: { pending: false, errors: null, warnings: null, explicit: false },
  general: { pending: false, errors: null },
  isrc: { pending: false, error: null },
};

export const initialState: WizardFeatureState = {
  dbLoading: false,
  asyncValidations: {
    eanUpc: { pending: false, errors: null },
    productTitle: { pending: false, errors: null, warnings: null, explicit: false },
    productTitleVersion: { pending: false, errors: null, warnings: null, explicit: false },
    tracks: {},
    trackWasAnalyzed: {},
    tracksPreview: { pending: false, errors: null },
    coverImages: { pending: false, allErrors: [] },
  },
  uploadProgress: {},
  trackDeletePending: [],
  existingTrackData: {},
  constraints: {
    loading: false,
    error: false,
    canGenerateEAN: false,
    scriptLanguages: [],
    audioLanguages: [],
    roles: [],
    rolesRequiringStoreIds: [],
    preselectedRoles: [],
    priceCodes: [],
    itunesPriceCodes: [],
    ageRatingRequired: false,
    bookletCLineRequired: false,
    canHaveThirdPartyAccounting: false,
    canHaveNeighbouringRights: false,
    allowedCountries: [],
    minTrackCount: undefined,
    minCoverWidth: undefined,
    minCoverHeight: undefined,
    canGenerateIsrc: false,
    clientId: '1',
  },
  value: {
    id: getUUID(),
    lastModified: Date.now(),
    stepProgress: undefined,
    productType: undefined,
    eanWasGenerated: false,
    productData: {
      title: '',
      titleVersion: '',
      coverImages: [],
      label: '',
      eanUpc: '',
      catalogNumber: '',
      language: '',
      genre: [undefined],
      pYear: '',
      pText: '',
      cYear: '',
      cText: '',
      description: '',
      price: '',
      // Spoken word specific fields
      seriesName: '',
      seriesNumber: '',
      bookletTitle: '',
      bookletCYear: '',
      bookletCText: '',
      bisacGenre: [undefined],
      wgsGenre: [undefined],
      ageRating: '',
      version: '',
      reviewQuotes: '',
      keywords: '',
      digitalPrice: '',
    },
    tracklisting: [],
    tracksProductTitleAdded: false,
    tracksPreview: undefined,
    releaseDates: {
      releaseDate: undefined,
      origReleaseDate: undefined,
      origReleaseDateToggle: false,
      tikTokReleaseDate: undefined,
      tikTokReleaseDateToggle: false,
      metaReleaseDate: undefined,
      metaReleaseDateToggle: false,
      preOrderReleaseDate: undefined,
      preOrderReleaseDateToggle: true,
      preReleaseSongsToggle: false,
      physicalProductReleaseDateToggle: false,
      physicalProductReleaseDate: new Date('2026-01-01'),
      price: '0,00',
      isbn: '',
      textReleaseDateToggle: true,
      textReleaseDate: new Date('2026-01-01'),
      spokenWordCopyrightYear: '',
      spokenWordCopyrightReleaseDate: '',
    },
    contributors: [],
    productContributors: [],
    overallMetadata: {
      ...defaultCommonTrackMetadata,
      appleDigitalMastered: false,
      appleEmail: '',
    },
    countrySelection: { countries: [] },
    shopSelection: { allShops: true, shopSelection: [], sendToNewShops: true },
    neighbouringRightsSelection: { evaluate: false, countries: [], assignments: [] },
    contractSelection: { thirdPartyContracts: [] },
  },
};

export enum WizardStep {
  SESSION_SELECT,
  PRODUCT_TYPE_SELECT,
  PRODUCT_DATA,
  TRACKLISTING,
  CONTRIBUTORS,
  TRACK_METADATA,
  RELEASE,
  COUNTRIES_SHOPS,
  SUMMARY,
}
